import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Sparkles, Utensils, BookOpen, Palette, Users, Home as HomeIcon,
  MapPin, Calendar, CheckCircle2, ArrowRight, ChefHat, Sun
} from 'lucide-react';
import CareBnBBookingForm from '../components/CareBnBBookingForm';

const IS_THIS_YOU = [
  "Is this your first time leaving your parents' home?",
  "Are you learning a lot of new information but not fully sure how to integrate it into your life?",
  "Are you starting to date seriously, and could use some advice?",
  "Are you gaining unwanted weight because you're not sure how to cook?",
];

const SCHEDULE = [
  { time: "Thursday · 5:00 PM", event: "Arrive at the BnB in Efrat. Settle in. You have your own kitchen — make your own food, at your own pace.", icon: HomeIcon },
  { time: "Thursday · 7:30–9:00 PM", event: "Group Circle of Life + breakout session. Take home a goals sheet as your reflection homework.", icon: Users },
  { time: "Friday Morning", event: "Join Chanah in the kitchen to learn how to make Shabbos — hands-on, no judgment, no diet agenda.", icon: ChefHat },
  { time: "Shabbat", event: "Shul + shiurim with Rabbi Shlomo Katz in the Shirat David community. Join Chanah's family for meals.", icon: Sun },
  { time: "Motzei Shabbos", event: "Wrap up with a Circle of Life reflection. Leave rooted, restored, with practical tools.", icon: Sparkles },
];

const CareBnB = () => {
  const [showForm, setShowForm] = useState(false);

  if (showForm) {
    return <CareBnBBookingForm onClose={() => setShowForm(false)} />;
  }

  return (
    <div className="carebnb-page" data-testid="carebnb-page">
      {/* Header */}
      <header className="header-nav">
        <div className="header-content">
          <Link to="/" data-testid="cb-logo-link">
            <img src="/brand/logo.png" alt="Momshiach" className="logo-img" />
          </Link>
          <nav className="desktop-nav">
            <Link to="/" className="nav-link" data-testid="cb-nav-home">Home</Link>
            <Link to="/carebnb" className="nav-link" data-testid="cb-nav-carebnb">CareBnB</Link>
            <Link to="/resources" className="nav-link" data-testid="cb-nav-resources">Resources</Link>
          </nav>
          <div className="nav-actions">
            <a
              href="https://wa.me/972529326458"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary btn-contact"
              data-testid="cb-header-contact"
            >
              <Phone size={16} />
              <span>Contact</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="carebnb-hero">
        <div className="container">
          <div className="carebnb-eyebrow" data-testid="carebnb-eyebrow">
            <Sparkles size={14} />
            <span>Self-Care Getaway · Efrat, Israel</span>
          </div>
          <h1 className="heading-hero" data-testid="carebnb-hero-title">
            CareBnB<span className="carebnb-em">.</span><br />
            Shabbat Chavaya
          </h1>
          <p className="carebnb-subhead body-large">
            A taste of Rav Shlomo Katz's community + personal development.<br />
            Thursday evening through Motzei Shabbos — a reboot for your body, mind, and neshama.
          </p>
          <div className="hero-cta-group">
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary btn-lg"
              data-testid="carebnb-hero-book-btn"
            >
              Book Your Getaway
            </button>
            <a href="#program" className="btn-secondary btn-lg">See What's Included</a>
          </div>
          <div className="carebnb-hero-meta">
            <div><Calendar size={16} /> Thurs 5PM → Motzei Shabbos</div>
            <div><MapPin size={16} /> Efrat, Israel</div>
            <div><Users size={16} /> 1–2 guests · No kids · Friends or couples</div>
          </div>
        </div>
      </section>

      {/* Rav Shlomo Katz Endorsement (near top for trust) */}
      <section className="carebnb-section endorsement-section" data-testid="rav-endorsement">
        <div className="container">
          <div className="endorsement-card">
            <div className="endorsement-quote-mark">"</div>
            <p className="body-large endorsement-text">
              Everyone wants to feel heard, but not everyone understands that being heard begins with ourselves. Do we really understand all that is transpiring in our mind, heart and soul?
            </p>
            <p className="body-large endorsement-text">
              In every generation, the Ribbono shel Olam places certain people, gifted with the ability to provide this gift for others. My dear friends, <strong>Chanah &amp; Pesach Rothberg</strong>, have dedicated their lives to create the space for the authentic soul seeker to understand the inner conversations of our true selves, and develop a healthy approach which is geared toward each person individually.
            </p>
            <p className="body-large endorsement-text">
              Through working with them, one will be blessed to begin to experience sincere joy in becoming an Eved Hashem.
            </p>
            <div className="endorsement-attribution">
              <div className="endorsement-line" />
              <div>
                <p className="endorsement-name">Rav Shlomo Katz</p>
                <p className="endorsement-role">Shirat David, Efrat</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Is this you? */}
      <section className="carebnb-section carebnb-questions">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Is this you?</h2>
          </div>
          <div className="questions-grid">
            {IS_THIS_YOU.map((q, i) => (
              <div key={i} className="question-card" data-testid={`carebnb-question-${i}`}>
                <div className="question-number">{i + 1}</div>
                <p className="body-medium">{q}</p>
              </div>
            ))}
          </div>
          <div className="questions-callout">
            <p className="body-large">
              <strong>If you answered yes to any of these, this program is for you.</strong>
            </p>
            <p className="body-medium">Especially targeted for <strong>midrasha students</strong> and young women stepping into their next chapter.</p>
          </div>
        </div>
      </section>

      {/* Add Color Philosophy */}
      <section className="carebnb-section carebnb-philosophy">
        <div className="container">
          <div className="philosophy-card" data-testid="carebnb-philosophy-card">
            <Palette size={40} color="var(--brand-gold)" />
            <h2 className="heading-1">We don't remove — we add color.</h2>
            <p className="body-large">
              All of the work we do with food is about learning how to incorporate <strong>colorful, plant-based foods</strong> into your <em>already existing plate</em> and lifestyle. I do not judge what you're eating. I do not come with an agenda for any certain type of diet.
            </p>
            <p className="body-medium">
              My only mission: help you <strong>add more color</strong> to your existing plate. That's it.
            </p>
          </div>
        </div>
      </section>

      {/* Program Schedule */}
      <section id="program" className="carebnb-section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Your Shabbat Chavaya</h2>
            <p className="body-large text-secondary">Thursday night through Motzei Shabbos</p>
          </div>
          <div className="schedule-timeline">
            {SCHEDULE.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="schedule-item" data-testid={`schedule-item-${i}`}>
                  <div className="schedule-icon"><Icon size={20} /></div>
                  <div className="schedule-content">
                    <p className="schedule-time">{item.time}</p>
                    <p className="body-medium">{item.event}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="between-sessions">
            <HomeIcon size={22} />
            <p className="body-medium">
              <strong>Between sessions, the BnB is yours.</strong> Read, sleep, rest, get work done, walk the streets of Efrat. This is a space to <em>reboot and recharge</em>.
            </p>
          </div>
        </div>
      </section>

      {/* Whats Included */}
      <section className="carebnb-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">What's Included</h2>
          </div>
          <div className="included-grid">
            <div className="included-card" data-testid="included-bnb">
              <HomeIcon size={28} color="var(--brand-lilac-deep)" />
              <h3 className="heading-3">Your own private BnB</h3>
              <p className="body-small">Full kitchen, quiet space to work / read / sleep. In beautiful Efrat.</p>
            </div>
            <div className="included-card" data-testid="included-smoothie">
              <Palette size={28} color="var(--brand-gold)" />
              <h3 className="heading-3">Morning green smoothies</h3>
              <p className="body-small">Prepared fresh by Chanah — a colorful start to each day.</p>
            </div>
            <div className="included-card" data-testid="included-kitchen">
              <ChefHat size={28} color="var(--brand-teal)" />
              <h3 className="heading-3">Friday cooking lesson</h3>
              <p className="body-small">Learn to prepare Shabbos in Chanah's kitchen — practical, hands-on.</p>
            </div>
            <div className="included-card" data-testid="included-shul">
              <Sun size={28} color="var(--brand-gold)" />
              <h3 className="heading-3">Shul + Shabbos in Shirat David</h3>
              <p className="body-small">Shiurim with Rabbi Shlomo Katz. Join Chanah's family for Shabbos meals.</p>
            </div>
            <div className="included-card" data-testid="included-circle">
              <Users size={28} color="var(--brand-lilac-deep)" />
              <h3 className="heading-3">Circle of Life session</h3>
              <p className="body-small">Group + breakout coaching. Take-home goals sheet.</p>
            </div>
            <div className="included-card" data-testid="included-cookbook">
              <BookOpen size={28} color="var(--brand-teal)" />
              <h3 className="heading-3">Published cookbook</h3>
              <p className="body-small">Chanah's cookbook / course book yours to take home.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="carebnb-section carebnb-pricing">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Reserve Your Spot</h2>
            <p className="body-large text-secondary">1,800 NIS per person · 10% off for the second guest</p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card" data-testid="pricing-solo">
              <div className="pricing-tag">Solo Reset</div>
              <h3 className="heading-2">1 Guest</h3>
              <div className="pricing-amount">1,800 <span>NIS</span></div>
              <ul className="pricing-features">
                <li>Full CareBnB program</li>
                <li>Private BnB</li>
                <li>All meals with Chanah's family Shabbos</li>
                <li>Cookbook + goals sheet</li>
              </ul>
            </div>
            <div className="pricing-card featured" data-testid="pricing-duo">
              <div className="pricing-tag gold">Best Value</div>
              <h3 className="heading-2">2 Guests</h3>
              <div className="pricing-amount">3,420 <span>NIS</span></div>
              <p className="pricing-sub">1,800 first guest + 1,620 second guest <em>(10% off)</em></p>
              <ul className="pricing-features">
                <li>Everything in Solo</li>
                <li>Perfect for friends or couples</li>
                <li>Share the private BnB (up to 2)</li>
                <li>No kids — this is your reset</li>
              </ul>
            </div>
          </div>

          <div className="deposit-note" data-testid="deposit-note">
            <CheckCircle2 size={20} color="var(--brand-gold)" />
            <div>
              <p className="body-medium"><strong>Secure your dates with a 500 NIS deposit today.</strong></p>
              <p className="body-small">Balance is due upon arrival. Deposit paid via secure PayPal checkout (card or PayPal).</p>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary btn-lg"
              data-testid="carebnb-pricing-book-btn"
            >
              Book Your Getaway <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="carebnb-section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Optional Add-Ons</h2>
            <p className="body-large text-secondary">Deepen your experience</p>
          </div>
          <div className="addons-grid">
            <div className="addon-card" data-testid="addon-energy">
              <Sparkles size={24} color="var(--brand-lilac-deep)" />
              <h3 className="heading-3">Energy Therapy — 6 Session Package</h3>
              <div className="addon-price">3,000 NIS <span className="addon-was">was 3,570</span></div>
              <p className="body-small">Passive, remote sessions. No time commitment. See our full energy therapy explanation on the home page.</p>
            </div>
            <div className="addon-card" data-testid="addon-narrative">
              <BookOpen size={24} color="var(--brand-teal)" />
              <h3 className="heading-3">Narrative Therapy Session</h3>
              <div className="addon-price">Inquire</div>
              <p className="body-small">1:1 session with Chanah during your stay to reframe your story.</p>
            </div>
            <div className="addon-card" data-testid="addon-catered">
              <Utensils size={24} color="var(--brand-gold)" />
              <h3 className="heading-3">Catered Meals</h3>
              <div className="addon-price">50 NIS <span>per meal</span></div>
              <p className="body-small">Don't want to cook every meal? Add fresh, colorful catered meals — choose how many.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coaching Package Note */}
      <section className="carebnb-section carebnb-coaching">
        <div className="container">
          <div className="coaching-callout" data-testid="coaching-callout">
            <h2 className="heading-2">Your journey continues after you leave.</h2>
            <p className="body-large">
              1 health coaching session <strong>during</strong> your CareBnB + <strong>11 more</strong> follow-up sessions when you're back home — a full 12-session coaching arc to help you integrate what you've learned.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Reboot. Recharge. Reconnect.</h2>
            <p className="body-large">Practical tools, kosher framework, colorful food, and a Shabbos in one of Israel's warmest communities.</p>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary btn-lg"
              data-testid="carebnb-final-book-btn"
            >
              Reserve Your Shabbat Chavaya
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-bottom">
            <p className="caption">© 2025 Momshiach · CareBnB Shabbat Chavaya · Efrat, Israel</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CareBnB;
